/**
 * Phase 5 (Feb 2026) — Aggregation-cap consumption badge.
 *
 * Reads GET /api/v1/policy-guard/aggregation-status and renders a
 * plain-german hint: "Heute bereits X% des Erhöhungs-Rahmens
 * verbraucht". Above 100% or with 0 remaining a stronger note surfaces
 * ("Weitere Erhöhungen heute erfordern menschliche Freigabe (Klasse
 * C)"), matching the concept §5 phrasing.
 *
 * Silent-fail on network errors — the badge simply hides itself so it
 * never blocks the page it's embedded on.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";

export default function AggregationConsumedBadge({
  variant = "inline",
  refreshKey,
}) {
  const [status, setStatus] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get("/policy-guard/aggregation-status");
        if (!cancelled) setStatus(r.data);
      } catch (_e) {
        if (!cancelled) setErrored(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (errored || !status || status.has_budget === false) return null;
  const pct = Math.max(0, Math.min(1.5, Number(status.consumed_pct) || 0));
  const pctInt = Math.round(pct * 100);
  const exhausted = pct >= 1.0 || (status.remaining_cents || 0) <= 0;
  const heavy = pct >= 0.75;

  const wrapperClass = variant === "block"
    ? "rounded-md border px-3 py-2 space-y-1 text-xs"
    : "flex items-center gap-2 text-xs";

  const barTone = exhausted
    ? "bg-destructive"
    : heavy
      ? "bg-amber-500"
      : "bg-emerald-500";

  return (
    <div
      data-testid="aggregation-consumed"
      data-exhausted={exhausted ? "true" : "false"}
      data-heavy={heavy ? "true" : "false"}
      className={wrapperClass}
    >
      <div className="flex items-center gap-2">
        <span
          data-testid="aggregation-consumed-pct"
          className="font-mono"
        >
          {pctInt}%
        </span>
        <span
          data-testid="aggregation-consumed-text"
          className="text-muted-foreground"
        >
          Heute bereits {pctInt}% des Erhöhungs-Rahmens verbraucht
        </span>
      </div>
      {variant === "block" && (
        <div
          className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
          data-testid="aggregation-consumed-bar"
        >
          <div
            style={{ width: `${Math.min(100, pctInt)}%` }}
            className={`h-full ${barTone}`}
          />
        </div>
      )}
      {exhausted && (
        <div
          data-testid="aggregation-consumed-exhausted-hint"
          className="text-destructive"
        >
          Weitere Erhöhungen heute erfordern menschliche Freigabe (Klasse C)
        </div>
      )}
    </div>
  );
}
